// ── CSC (UCC) integration settings ───────────────────────────────────────────
//
// Where the three CSC credentials go, and the only place they are ever entered.
//
// SECRETS ARE WRITE-ONLY HERE. What you type is pushed to Key Vault and never
// read back — not by this page, not by any endpoint that answers a browser. The
// boxes show whether a secret is SET, never what it is. A field that redisplayed
// a client secret would put it in every screenshot, every session recording and
// every browser cache that ever held this page.
//
// The environment is part of the credential set, not a separate setting: CSC
// issues different keys for UAT and Production, and the URLs are pinned in
// api/shared/cscClient.js rather than typed here so a mistyped host cannot point
// a real filing at a sandbox or a test at the real thing.
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { Btn, Card, ErrorMsg, FInput, Field, SectionHeader, Spinner, Td, Th } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

const PROVIDER = "csc";

// Must match SECRETS in api/shared/cscClient.js. Named here as constants rather
// than typed into the payload so a rename is one edit in each place and a
// mismatch is visible instead of silently writing a secret nobody reads.
const SECRET_APIKEY = "csc-apikey";
const SECRET_CLIENTID = "csc-clientid";
const SECRET_CLIENTSECRET = "csc-clientsecret";

const ENVIRONMENTS = [
  { value: "uat",        label: "UAT — testing. Filings are not really processed." },
  { value: "dev",        label: "Dev — initial integration testing." },
  { value: "production", label: "Production — filings are REAL." },
];

export const IntegrationCSCPage = () => {
  const { api } = useApp();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [row, setRow] = useState(null);

  const [environment, setEnvironment] = useState("uat");
  const [isactive, setIsactive] = useState(false);
  const [apikey, setApikey] = useState("");
  const [clientid, setClientid] = useState("");
  const [clientsecret, setClientsecret] = useState("");

  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(null);

  const load = () => {
    setLoading(true); setErr(null);
    api.get("getIntegrations", { provider: PROVIDER })
      .then(d => {
        setRow(d || null);
        const cfg = (d && d.config) || {};
        setEnvironment(cfg.environment || "uat");
        setIsactive(!!(d && d.isactive));
        setLoading(false);
      })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const status = (row && row.secretstatus) || {};
  const have = n => !!status[n];
  const allThree = have(SECRET_APIKEY) && have(SECRET_CLIENTID) && have(SECRET_CLIENTSECRET);

  const save = async () => {
    setSaving(true); setNote(null); setTest(null);
    try {
      // Only non-empty boxes are sent. A blank field means "leave what is
      // already in the vault alone", which is what lets somebody change the
      // environment without re-typing three secrets.
      const secrets = {};
      if (apikey.trim())       secrets[SECRET_APIKEY] = apikey.trim();
      if (clientid.trim())     secrets[SECRET_CLIENTID] = clientid.trim();
      if (clientsecret.trim()) secrets[SECRET_CLIENTSECRET] = clientsecret.trim();

      const res = await api.post("saveIntegration", {
        provider: PROVIDER,
        displayname: "CSC — UCC Business Direct",
        config: {
          environment,
          // Recorded so the Data Model and anyone reading the row can see which
          // vault entries this provider owns without opening the code.
          secretnames: [SECRET_APIKEY, SECRET_CLIENTID, SECRET_CLIENTSECRET],
        },
        secrets,
        isactive,
      });
      setApikey(""); setClientid(""); setClientsecret("");
      setRow(r => ({ ...(r || {}), secretstatus: res.secretstatus || {} }));
      setNote("Saved." + (Object.keys(secrets).length
        ? " " + Object.keys(secrets).length + " secret" + (Object.keys(secrets).length === 1 ? "" : "s")
          + " written to Key Vault."
        : " No secrets changed."));
      load();
    } catch (e) { setNote("Not saved — " + e.message); }
    setSaving(false);
  };

  const runTest = async () => {
    setTesting(true); setTest(null);
    try {
      const res = await api.post("cscTestConnection", {});
      setTest(res);
    } catch (e) { setTest({ ok: false, error: e.message }); }
    setTesting(false);
  };

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const inp = { width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 13,
                borderRadius: 6, border: "1px solid #ccc" };
  const chip = ok => ({ fontSize: 11.5, padding: "2px 8px", borderRadius: 4, fontWeight: 600,
                        background: ok ? "#E1F5EE" : "#FBE4DD", color: ok ? "#0F6E56" : "#993C1D" });

  return <>
    <SectionHeader title="CSC — UCC Business Direct" />

    <Card style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 12.5, color: "var(--color-text-secondary)", marginBottom: 14 }}>
        CSC issues three values: an <strong>API key</strong>, a <strong>client ID</strong> and a{" "}
        <strong>client secret</strong>. They are specific to an environment — the UAT set will not work
        against Production. Enter them once here; they are written to Key Vault and never displayed again.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px", maxWidth: 760 }}>
        <div style={{ gridColumn: "1/-1", marginBottom: 12 }}>
          <Field label="Environment">
            <select value={environment} onChange={e => setEnvironment(e.target.value)} style={inp}>
              {ENVIRONMENTS.map(x => <option key={x.value} value={x.value}>{x.label}</option>)}
            </select>
          </Field>
        </div>

        <div>
          <Field label="API key">
            <FInput value={apikey} onChange={setApikey}
                    placeholder={have(SECRET_APIKEY) ? "•••••••• (leave blank to keep)" : "Paste the API key"} />
          </Field>
          <div style={{ marginTop: -8, marginBottom: 10 }}>
            <span style={chip(have(SECRET_APIKEY))}>{have(SECRET_APIKEY) ? "set" : "not set"}</span>
            <span style={{ fontSize: 11, color: "var(--color-text-secondary)", marginLeft: 8 }}>
              Key Vault · {SECRET_APIKEY}
            </span>
          </div>
        </div>

        <div>
          <Field label="Client ID">
            <FInput value={clientid} onChange={setClientid}
                    placeholder={have(SECRET_CLIENTID) ? "•••••••• (leave blank to keep)" : "Paste the client ID"} />
          </Field>
          <div style={{ marginTop: -8, marginBottom: 10 }}>
            <span style={chip(have(SECRET_CLIENTID))}>{have(SECRET_CLIENTID) ? "set" : "not set"}</span>
            <span style={{ fontSize: 11, color: "var(--color-text-secondary)", marginLeft: 8 }}>
              Key Vault · {SECRET_CLIENTID}
            </span>
          </div>
        </div>

        <div>
          <Field label="Client secret">
            <FInput value={clientsecret} onChange={setClientsecret}
                    placeholder={have(SECRET_CLIENTSECRET) ? "•••••••• (leave blank to keep)" : "Paste the client secret"} />
          </Field>
          <div style={{ marginTop: -8, marginBottom: 10 }}>
            <span style={chip(have(SECRET_CLIENTSECRET))}>{have(SECRET_CLIENTSECRET) ? "set" : "not set"}</span>
            <span style={{ fontSize: 11, color: "var(--color-text-secondary)", marginLeft: 8 }}>
              Key Vault · {SECRET_CLIENTSECRET}
            </span>
          </div>
        </div>

        <div style={{ alignSelf: "center" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={isactive} onChange={e => setIsactive(e.target.checked)} />
            Enabled — ACE may call CSC
          </label>
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 4 }}>
            Off by default. Nothing calls CSC while this is off, including the test below.
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 16 }}>
        <Btn variant="primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</Btn>
        <Btn disabled={testing || !allThree || !isactive} onClick={runTest}>
          {testing ? "Testing…" : "Test connection"}
        </Btn>
        {!allThree && (
          <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
            All three secrets have to be saved before the connection can be tested.
          </span>
        )}
        {allThree && !isactive && (
          <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
            Enable the integration to test it.
          </span>
        )}
      </div>

      {note && <div style={{ fontSize: 12.5, marginTop: 12, background: "var(--color-background-secondary)",
                             border: "0.5px solid var(--color-border-tertiary)", borderRadius: 8,
                             padding: "8px 12px" }}>{note}</div>}
    </Card>

    {test && (
      <Card style={{ padding: 16, marginBottom: 16,
                     borderLeft: "3px solid " + (test.ok ? "#0F6E56" : "#993C1D") }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>
          {test.ok ? "Connected" : "Could not connect"}
          {typeof test.ms === "number" && (
            <span style={{ fontWeight: 400, fontSize: 12, color: "var(--color-text-secondary)", marginLeft: 8 }}>
              {test.ms} ms
            </span>
          )}
        </div>
        {test.ok ? <>
          <div style={{ fontSize: 13, marginBottom: 10 }}>
            Authenticated against <strong>{test.label}</strong> and read{" "}
            <strong>{test.jurisdictionCount}</strong> filing jurisdictions
            {typeof test.billingRefCount === "number" && <> · {test.billingRefCount} billing reference{test.billingRefCount === 1 ? "" : "s"}</>}.
          </div>
          {test.billingNote && (
            <div style={{ fontSize: 12, color: "#7a5200", background: "#FAEEDA",
                          border: "0.5px solid #EBD3A0", borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
              {test.billingNote}
            </div>
          )}
          {!!(test.sample || []).length && (
            <table style={{ borderCollapse: "collapse", fontSize: 12.5 }}>
              <thead><tr><Th>State</Th><Th>Jurisdiction</Th><Th>Office</Th><Th>sourceId</Th></tr></thead>
              <tbody>{test.sample.map((s, i) => (
                <tr key={i}>
                  <Td>{s.state}</Td><Td>{s.name}</Td><Td>{s.office}</Td>
                  <Td><span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5 }}>{s.sourceId}</span></Td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </> : (
          <div style={{ fontSize: 13, color: "#993C1D" }}>{test.error}</div>
        )}
      </Card>
    )}

    {/* Said on the page, not only in a commit message. Somebody arriving here
        with a fresh set of credentials should be able to tell what enabling this
        does and does not permit. */}
    <Card style={{ padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>What this does, and does not, allow</div>
      <div style={{ fontSize: 12.5, color: "var(--color-text-secondary)", lineHeight: 1.6 }}>
        Enabling CSC lets ACE <strong>read</strong>: order status, orders that have changed, and filed
        documents. A scheduled job may do that, because reading changes nothing at a filing office.
        <br />
        It does <strong>not</strong> let anything order a search, create a filing, submit one or terminate
        one. Those spend money and make a legal record, so they happen only when a person asks for them —
        and that is enforced in the code, not just in policy: the scheduled path physically has no method
        that can place an order.
        <br /><br />
        <span style={{ color: B.cyanDark, fontWeight: 600 }}>Nothing is ordered automatically. Ever.</span>
      </div>
    </Card>
  </>;
};
